import { useEffect } from "react";

const isMac =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform);

const MODIFIER_LABELS: Record<string, string> = {
  mod: isMac ? "⌘" : "Ctrl",
  shift: isMac ? "⇧" : "Shift",
  alt: isMac ? "⌥" : "Alt",
};

const MODIFIER_CHECKS: Record<string, (event: KeyboardEvent) => boolean> = {
  mod: (event) => (isMac ? event.metaKey : event.ctrlKey),
  shift: (event) => event.shiftKey,
  alt: (event) => event.altKey,
};

type UseKeyboardShortcutOptions = {
  enabled?: boolean;
};

export function useKeyboardShortcut(
  shortcut: string,
  handler: () => void,
  { enabled = true }: UseKeyboardShortcutOptions = {},
) {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const parts = shortcut.toLowerCase().split("+");
    const modifiers = parts.filter((part) => part in MODIFIER_CHECKS);
    const mainKey = parts.find((part) => !(part in MODIFIER_CHECKS));

    function handleKeyDown(event: KeyboardEvent) {
      if (!mainKey || event.key.toLowerCase() !== mainKey) {
        return;
      }

      if (!modifiers.every((modifier) => MODIFIER_CHECKS[modifier](event))) {
        return;
      }

      event.preventDefault();
      handler();
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [shortcut, handler, enabled]);
}

export function formatShortcutKeys(shortcut: string): string[] {
  return shortcut
    .split("+")
    .map((part) => MODIFIER_LABELS[part.toLowerCase()] ?? part.toUpperCase());
}
